import { take, takeUntil } from 'rxjs';

import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IClient } from '@clients/models/firebase/client.model';
import { JobCardOffcanvasComponent } from '@job-card/components/job-card-offcanvas/job-card-offcanvas.component';
import { AssessmentScheduleViewSteps, leadAssessmentStatusOptions } from '@leads/client-facing/models/domain/scheduled-assessment.domain';
import { LeadRequestType } from '@leads/shared/models/domain/lead-request.domain';
import { ScheduledAssessment, ScheduledAssessmentStatus } from '@leads/shared/models/domain/scheduled-assessment.domain';
import { IDropdownOption } from '@shared/components/forms/dropdown-control/dropdown-control.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { IStepNavigation, IStepNavigatorConfig, NavButtonStyle, StepDisplay, StepModes, StepUI } from '@shared/models/step-navigator';
import { ModalService } from '@shared/services/modal/modal.service';
import { OffcanvasService } from '@shared/services/offcanvas/offcanvas.service';
import { WhatsappService } from '@shared/services/whatsapp/whatsapp.service';

import { internalNotesPopOver, jobDescriptionPopOver } from './infopopover';

export type ScheduledAssessmentStatusChange = {
	assessmentId: string;
	status: ScheduledAssessmentStatus;
};

export type ScheduledAssessmentEmployeeIdChange = {
	assessmentId: string;
	employeeId: string;
};

export type ScheduledAssessmentNotesChange = {
	assessmentId: string;
	assessmentNotes: string;
};

export type ScheduledAssessmentJobDescriptionChange = {
	assessmentId: string;
	jobDescription: string;
};

@Component({
	template: '',
})
export abstract class BaseScheduledItemDetailComponent implements OnInit, OnDestroy {
	@Input() scheduledItem: ScheduledAssessment;
	@Input() employees: Array<IDropdownOption> = [];
	@Input() client: IClient;
	@Input() readonlyView = false;
	@Input() isSavingNotes = false;
	@Input() isSavingJobDescription = false;

	@Output() statusChanged = new EventEmitter<ScheduledAssessmentStatusChange>();
	@Output() employeeIdChanged = new EventEmitter<ScheduledAssessmentEmployeeIdChange>();
	@Output() assessmentNotesChanged = new EventEmitter<ScheduledAssessmentNotesChange>();
	@Output() jobDescriptionChanged = new EventEmitter<ScheduledAssessmentJobDescriptionChange>();

	protected leadAssessmentStatus: ScheduledAssessmentStatus;
	protected readonly leadAssessmentStatusOptions = leadAssessmentStatusOptions;
	readonly jobDescriptionPopOver = jobDescriptionPopOver;
	readonly internalNotesPopOver = internalNotesPopOver;

	assessmentForm = new FormGroup({
		whatsappNotes: new FormControl<string | null | undefined>(''),
		employeeId: new FormControl<string | null>(null, [Validators.required]),
		assessmentNotes: new FormControl<string | null | undefined>(''),
		jobDescription: new FormControl<string | null | undefined>(''),
	});

	NavigationStepCases = AssessmentScheduleViewSteps;
	LeadRequestType = LeadRequestType;
	navigationConfig: IStepNavigatorConfig;
	selectedStep: IStepNavigation;

	isLoading = true;
	destroyed$ = new EventEmitter<void>();

	protected whatsAppService = inject(WhatsappService);
	private modalService = inject(ModalService);
	protected offcanvasService = inject(OffcanvasService);

	ngOnInit(): void {
		this.navigationConfig = {
			ui: StepUI.WIZARD,
			mode: StepModes.NG_SWITCH,
			displayType: StepDisplay.AUTO,
			navButtonStyle: NavButtonStyle.CHEVRON,
			showStepChevrons: true,
			showStepNumbers: false,
			showNavButtons: true,
			steps: [
				{
					title: {
						full: 'Assessment Overview',
						tabs: 'Assessment Overview',
					},
					icon: 'bi-clipboard',
					switchCase: AssessmentScheduleViewSteps.ASSESSMENT_OVERVIEW,
				},
				{
					title: {
						full: 'Communications',
						tabs: 'Communications',
					},
					icon: 'bi-telephone',
					switchCase: AssessmentScheduleViewSteps.COMMUNICATIONS,
				},
			],
		};

		this.selectedStep = this.navigationConfig.steps[0];

		this.leadAssessmentStatus = this.scheduledItem.status;

		this.assessmentForm.patchValue({
			whatsappNotes: this.scheduledItem.whatsappNotes,
			employeeId: this.scheduledItem.assignedEmployee.userId,
			assessmentNotes: this.scheduledItem.assessmentDetails.internalAssessmentNotes,
			jobDescription: this.scheduledItem.assessmentDetails.jobDescription,
		});

		this.handleReadonlyView();
		this.onEmployeeChange();

		this.isLoading = false;
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	private handleReadonlyView() {
		if (this.readonlyView) {
			this.assessmentForm.disable();
		} else {
			this.assessmentForm.enable();
		}
	}

	statusChange(status: ScheduledAssessmentStatus): void {
		this.scheduledItem.status = status;
		this.statusChanged.emit({ assessmentId: this.scheduledItem.id!, status });
	}

	shareToWhatsApp(): void {
		const text = this.assessmentForm.controls.whatsappNotes.value;

		this.whatsAppService.startChatWithContact({
			contactName: this.client.clientName,
			contactNumber: this.client.clientNumber,
			messageText: text!,
		});
	}

	onEmployeeChange(): void {
		this.assessmentForm.controls.employeeId.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
			this.employeeIdChanged.emit({ assessmentId: this.scheduledItem.id!, employeeId: value! });
		});
	}

	onAssessmentNotesChange(): void {
		this.assessmentNotesChanged.emit({
			assessmentId: this.scheduledItem.id!,
			assessmentNotes: this.assessmentForm.controls.assessmentNotes.value!,
		});
	}

	onJobDescriptionChange(): void {
		this.jobDescriptionChanged.emit({
			assessmentId: this.scheduledItem.id!,
			jobDescription: this.assessmentForm.controls.jobDescription.value!,
		});
	}

	openJobCard(): void {
		const shouldClose = (): Promise<boolean> => {
			return new Promise((resolve) => {
				if (componentInstance.form.dirty) {
					const cmRef = this.modalService.open(ConfirmationModalComponent, false, 'md');
					const cmComponentInstance = cmRef.componentInstance as ConfirmationModalComponent;
					cmComponentInstance.title = 'Unsaved Changes - Do You Want to Discard?';
					cmComponentInstance.contentText = `You have unsaved changes in your job card. If you confirm, all changes will be lost. To continue editing or save your changes, click Back.`;
					cmComponentInstance.confirmAction.pipe(take(1)).subscribe(() => resolve(true));
				} else {
					resolve(true);
				}
			});
		};
		const ref = this.offcanvasService.open(JobCardOffcanvasComponent, 'end', true, 'oc-lg', shouldClose);
		const componentInstance = ref.componentInstance as JobCardOffcanvasComponent;
		componentInstance.leadAssessmentId = this.scheduledItem.id!;
		componentInstance.isOwner = true;
	}
}
